import { useCallback, useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import ModalAtom from "../../../components/atoms/modal/modal";
import { FilterBar } from "../../../components/molecule/molecule";
import ProjectHeader from "../../../components/ProjectHeader";
import NoPermissionAlert from "../../../components/NoPermissionAlert";
import useProjectPermission from "../../../hooks/useProjectPermission";
import { downloadSupplierInfoPdf } from "../../../store/services/supplierInfoService";
import {
    fetchSupplierInfoForInvoice,
    saveSupplierInfo,
    selectSupplierInfo,
    selectSupplierInfoLoading,
} from "../../../store/slices/supplierInfoSlice";
import { showToast } from "../../../utils/method";
import CalculationTableV2 from "./components/CalculationTableV2";
import ContractSecuritySection from "./components/ContractSecuritySection";
import InvoiceOverviewSection from "./components/InvoiceOverviewSection";
import LegacyFinancialSections from "./components/LegacyFinancialSections";
import PdfSectionModal from "./components/PdfSectionModal";
import PerformancePeriodSection from "./components/PerformancePeriodSection";
import SecurityCalculationSection from "./components/SecurityCalculationSection";
import StatusBanners from "./components/StatusBanners";
import SupplementsSection from "./components/SupplementsSection";
import TicketDataSection from "./components/TicketDataSection";
import { ticketSections } from "./constants/ticketSections";
import { PROJECT_AREA, PROJECT_ACTION } from "../../../constant/projectPermissions";
import "./supplier-info.scss";

function SupplierInfoPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { projectId, invoiceId } = useParams();

    const { project: projectDetails } = useSelector(
        (state) => state.projectDetails,
    );
    // Supplier-info is gated like aufmass: without invoice.manageSupplierInfo a
    // member is blocked entirely (the row action + workflow node are also gated
    // on it). This page-level guard backstops direct-URL navigation.
    const { can, isLoading: permissionLoading } = useProjectPermission();
    const canAccessSupplierInfo = can(PROJECT_AREA.INVOICE, PROJECT_ACTION.MANAGE_SUPPLIER_INFO);
    // Finalizing the sheet is a distinct, higher-risk lock action gated by the
    // separate invoice.finalize permission. Saving/editing only needs page access
    // (manageSupplierInfo) - the default member can manage but not finalize.
    const canFinalize = can(PROJECT_AREA.INVOICE, PROJECT_ACTION.FINALIZE);
    const [showFinalizeModal, setShowFinalizeModal] = useState(false);
    const [showPdfSectionModal, setShowPdfSectionModal] = useState(false);
    const [selectedLanguage, setSelectedLanguage] = useState("de"); // Add language state
    const [invoiceOverviewFilter, setInvoiceOverviewFilter] = useState("all"); // Add invoice overview filter state
    const [includeComments, setIncludeComments] = useState(false); // Include comments in invoice overview PDF
    const [pdfFileName, setPdfFileName] = useState(""); // Add PDF filename state

    // Create initial selected sections state dynamically
    const getInitialSelectedSections = () => {
        // Only include dynamic sections from ticketSections for selection
        const dynamicSections = {};
        ticketSections.forEach((section) => {
            dynamicSections[section.dataKey] = true;
        });

        dynamicSections.introductionPage = true;
        dynamicSections.supplements = true;
        dynamicSections.paymentHistory = true;
        dynamicSections.invoiceOverview = true;

        return dynamicSections;
    };

    const [selectedSections, setSelectedSections] = useState(
        getInitialSelectedSections(),
    );

    // Use the redux selectors
    const supplierInfo = useSelector(selectSupplierInfo);
    const loading = useSelector(selectSupplierInfoLoading);

    // Legacy: stored records saved before schemaVersion 2 use the old multi-card layout.
    // Fresh data (isStored = false) and new stored records (schemaVersion present) use the tabular view.
    const isLegacySchema = supplierInfo?.isStored && !supplierInfo?.schemaVersion;

    // Function to generate default filename
    const getDefaultFileName = useCallback(() => {
        const referenceNumber = supplierInfo?.invoice?.internalReferenceNumber;
        const projectKey = projectDetails?.projectKey;
        const companyName = supplierInfo?.companyInfo?.name;

        if (referenceNumber && projectKey && companyName) {
            // Sanitize company name: replace invalid chars with hyphen, collapse multiple hyphens
            const sanitizedCompanyName = companyName
                .replace(/[/\\?%*:|"<>\s]/g, "-")  // Replace invalid chars and spaces with hyphen
                .replace(/-+/g, "-")                // Collapse multiple hyphens into one
                .replace(/^-+|-+$/g, "");           // Remove leading/trailing hyphens
            return `${referenceNumber}_${projectKey}_${sanitizedCompanyName}`;
        }

        return `supplier-info-${invoiceId}`;
    }, [supplierInfo, projectDetails, invoiceId]);

    // Generate default filename when supplierInfo is loaded
    useEffect(() => {
        if (
            supplierInfo?.invoice?.internalReferenceNumber &&
            projectDetails?.name
        ) {
            const defaultFileName = getDefaultFileName();
            setPdfFileName(defaultFileName);
        }
    }, [supplierInfo, projectDetails, getDefaultFileName]);

    useEffect(() => {
        if (!isLegacySchema) return;
        if (invoiceOverviewFilter === "exceeds100") {
            setInvoiceOverviewFilter("all");
        }
    }, [invoiceOverviewFilter, isLegacySchema]);

    // Fetch invoice data when component mounts
    useEffect(() => {
        if (invoiceId) {
            dispatch(fetchSupplierInfoForInvoice({ invoiceId }));
        }
    }, [dispatch, invoiceId]);

    // Update form data when invoice is loaded and create filter items based on status
    const FILTER_ITEMS = useMemo(() => {
        // Base items - always include back button and PDF download button
        const baseItems = [
            {
                type: "backButton",
                key: "back",
                label: t("common.back"),
            },
            {
                displayPosition: "right",
                type: "button",
                key: "download-pdf",
                labelKey: "common.download-pdf",
                className: "btn btn-secondary me-2",
                disabled: loading || !supplierInfo,
            },
        ];

        // If supplierInfo exists and is in FINALIZED status, only show back button and PDF download
        if (supplierInfo?.status === "FINALIZED") {
            return baseItems;
        }

        // Otherwise show full set of controls for draft mode. The finalize button
        // additionally requires the separate invoice.finalize permission.
        return [
            ...baseItems,
            ...(canFinalize
                ? [
                      {
                          displayPosition: "right",
                          type: "button",
                          key: "finalize",
                          labelKey: "common.finalize",
                          className: "btn btn-primary",
                          disabled: loading,
                      },
                  ]
                : []),
        ];
    }, [loading, t, supplierInfo, canFinalize]);

    const handleSubmit = async (e, finalize = false) => {
        e?.preventDefault();

        try {
            // Save the data
            await dispatch(saveSupplierInfo({ finalize })).unwrap();
        } catch (error) {
            console.error("Error saving data:", error);
        }
    };

    const downloadPdf = async (selectedSections) => {
        try {
            // Close the modal first
            setShowPdfSectionModal(false);

            // Use provided filename or fallback to default
            const finalFileName = pdfFileName?.trim() || getDefaultFileName();
            const effectiveInvoiceOverviewFilter =
                isLegacySchema && invoiceOverviewFilter === "exceeds100"
                    ? "all"
                    : invoiceOverviewFilter;

            // Call backend API to generate and download PDF with language, invoice overview filter, and filename
            await downloadSupplierInfoPdf(
                invoiceId,
                selectedSections,
                selectedLanguage,
                effectiveInvoiceOverviewFilter,
                finalFileName,
                includeComments,
                projectId,
            );
        } catch (error) {
            console.error("Error downloading PDF:", error);
            showToast(
                t(`errors.${error?.message}`) || "Error downloading PDF",
                { type: "error" },
            );
        }
    };

    const onFilterAction = (index, key) => {
        switch (key) {
            case "back":
                navigate(-1);
                break;
            case "cancel":
                navigate(-1);
                break;
            case "download-pdf":
                // Show PDF section selection modal
                setShowPdfSectionModal(true);
                break;
            case "finalize":
                setShowFinalizeModal(true);
                break;
            default:
                break;
        }
    };

    const confirmFinalize = () => {
        setShowFinalizeModal(false);
        handleSubmit(null, true);
    };

    const handleSectionToggle = (sectionKey) => {
        setSelectedSections((prev) => ({
            ...prev,
            [sectionKey]: !prev[sectionKey],
        }));
    };

    const handleSelectAllSections = () => {
        const allSelected = Object.values(selectedSections).every(Boolean);
        const newState = {};
        Object.keys(selectedSections).forEach((key) => {
            newState[key] = !allSelected;
        });
        setSelectedSections(newState);
    };

    const confirmPdfDownload = () => {
        setShowPdfSectionModal(false);
        // Call the new PDF download service
        downloadPdf(selectedSections);
    };

    const renderPdfSectionModal = () => {
        return (
            <PdfSectionModal
                selectedSections={selectedSections}
                onSectionToggle={handleSectionToggle}
                onSelectAllSections={handleSelectAllSections}
                ticketSections={ticketSections}
                selectedLanguage={selectedLanguage}
                onLanguageChange={setSelectedLanguage}
                invoiceOverviewFilter={invoiceOverviewFilter}
                onInvoiceOverviewFilterChange={setInvoiceOverviewFilter}
                includeComments={includeComments}
                onIncludeCommentsChange={setIncludeComments}
                pdfFileName={pdfFileName}
                onFileNameChange={setPdfFileName}
                defaultFileName={getDefaultFileName()}
                isV2Schema={!isLegacySchema}
            />
        );
    };

    if (!supplierInfo && loading) {
        return (
            <div className="supplier-info-page">
                <ProjectHeader projectName={projectDetails?.name} tabTitle={t("supplier-info.title")} />
                <div className="page-container">
                    <div className="si-skeleton">
                        {[180, 220, 160, 200, 170].map((h, i) => (
                            <div key={i} className="si-skeleton__section">
                                <span className="sk-line" style={{ width: "35%", height: "14px" }} />
                                <span className="sk-line" style={{ width: "100%", height: "11px" }} />
                                <span className="sk-line" style={{ width: "90%", height: "11px" }} />
                                <span className="sk-line" style={{ width: "95%", height: "11px" }} />
                                {h > 170 && <span className="sk-line" style={{ width: "85%", height: "11px" }} />}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (!permissionLoading && !canAccessSupplierInfo) {
        return (
            <div className="supplier-info-page">
                <ProjectHeader
                    projectName={projectDetails?.name || ""}
                    tabTitle={t("supplier-info.title")}
                />
                <NoPermissionAlert />
            </div>
        );
    }

    return (
        <div className="supplier-info-page">
            <ProjectHeader
                projectName={projectDetails?.name}
                tabTitle={t("supplier-info.title")}
            />

            <div className="page-container">
                <FilterBar items={FILTER_ITEMS} onFilterAction={onFilterAction} />

                {/* Status banners for different data states with conditional buttons */}
                <StatusBanners
                    supplierInfo={supplierInfo}
                    invoiceId={invoiceId}
                    handleSubmit={handleSubmit}
                />

                <div className="supplier-info-content">
                    <Row className="justify-content-center">
                        <Col md={10} xl={10}>
                            <h4 className="py-2 px-3 bg-warning text-center">
                                {t("supplier-info.invoice-coversheet")}
                            </h4>
                        </Col>
                    </Row>

                    {/* Financial Data Sections - legacy multi-card for old stored records; new 2-column layout for fresh/new */}
                    {isLegacySchema ? (
                        <LegacyFinancialSections />
                    ) : (
                        <Row className="justify-content-center">
                            <Col md={5}>
                                <CalculationTableV2 />
                            </Col>
                            <Col md={5}>
                                <PerformancePeriodSection />
                                <ContractSecuritySection />
                                <SecurityCalculationSection />
                            </Col>
                        </Row>
                    )}

                    <Row className="justify-content-center">
                        <Col md={10}>
                            <InvoiceOverviewSection />
                        </Col>
                    </Row>

                    {/* Add Amendments Section */}
                    <Row className="justify-content-center">
                        <Col md={10}>
                            <SupplementsSection />
                        </Col>
                    </Row>

                    {/* Render all ticket data sections in a loop */}
                    {ticketSections.map((section) => {
                        // Skip rendering sections with no data
                        const sectionData = supplierInfo?.[section.dataKey];

                        // Extract totalAmount calculation and color styling into variables
                        const totalAmount = sectionData?.totalAmount || 0;
                        const totalStyle = {
                            color:
                                totalAmount > 0
                                    ? "#198754" // Bootstrap success green
                                    : totalAmount < 0
                                        ? "#dc3545" // Bootstrap danger red
                                        : "#000000", // Black for zero
                            fontWeight: "bold",
                        };

                        return (
                            <Row
                                className="justify-content-center"
                                key={section.id}
                            >
                                <Col md={10}>
                                    <TicketDataSection
                                        title={section.title()}
                                        data={sectionData?.items || []}
                                        totalAmount={totalAmount}
                                        totalStyle={totalStyle}
                                        amountFieldHeaderClass={
                                            section.style.amountFieldHeaderClass
                                        }
                                        amountFieldCellStyle={
                                            section.style.amountFieldCellStyle
                                        }
                                    />
                                </Col>
                            </Row>
                        );
                    })}
                </div>
            </div>

            <ModalAtom
                show={showFinalizeModal}
                title={t("supplier-info.finalize-title")}
                body={t("supplier-info.finalize-message")}
                saveText={t("common.continue")}
                handleSubmit={confirmFinalize}
                handleClose={() => setShowFinalizeModal(false)}
            />

            <ModalAtom
                show={showPdfSectionModal}
                title={t("supplier-info.pdf-sections-title")}
                body={renderPdfSectionModal()}
                saveText={t("common.download")}
                handleSubmit={confirmPdfDownload}
                handleClose={() => setShowPdfSectionModal(false)}
                customClassName="pdf-section-modal"
                size="xl"
            />
        </div>
    );
}

export default SupplierInfoPage;
